import { Box, Container, Flex, Paper, Text, Button, Loader, Center } from "@mantine/core";
import {
  IconLock,
  IconArrowLeft,
  IconExclamationCircle,
} from "@tabler/icons-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDisclosure } from "@mantine/hooks";
import { successNotification } from "@/shared/utils/successNotification";
import ProfileTab from "./ProfileTab";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util.ts";
import { useAuth } from "@/hooks/useAuth";
import ShortlistModal from "@/components/shared/UserSignupModal";
import GenericModal from "@/components/shared/GenericModal";
import ExpressInterestModal, { ExpressInterestFormData } from "@/components/shared/ExpressInterestModal";
import { useUser } from "@/context/UserContext";
import InfoBanner from "@/components/shared/InfoBanner";
import { STATUS_COLORS, CollaborationStatus } from "@/components/shared/constants/general.const.ts";
import { PageID, PAGE_CONFIG } from "./AnonymousProfile.config";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import { downloadAnonymizedProfilePdf } from "@/utils/anonymizedProfilePdf";
import { errorNotification } from "@/shared/utils/errorNotification";
import PaymentWindowExtendModal from "@/components/shared/PaymentWindowExtendModal";
import {
  formatPaymentHoursLabel,
  isPaymentUiWarningUrgent,
  usePaymentWindowUiDemo,
} from "@/shared/utils/paymentWindowUiDemo";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import CompanyHeaderCard from "./CompanyHeaderCard";
import WhatWeOfferCard from "./WhatWeOfferCard";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";
import CollaborationPaymentSuccessModal from "@/pages/interests/components/CollaborationPaymentSuccessModal";
import { useCollaborationEngagementPayment } from "@/pages/interests/hooks/useCollaborationEngagementPayment";

const BOOKMARK_HEADER_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
};

interface AnonymousProfileProps {
  pageID: PageID;
  companyData?: any;
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function AnonymousProfile({
  pageID,
  companyData,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: AnonymousProfileProps) {
  const config = PAGE_CONFIG[pageID];

  const { backLink, backText, containerSize, hideBackButton, hideInfoBanner, hideInterestCTA, disableShortlist } =
    config;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [isFavorited, setIsFavorited] = useState(false);
  const [company, setCompany] = useState<any>(companyData || null);
  const [loading, setLoading] = useState(!companyData);
  const [collaborationStatus, setCollaborationStatus] = useState<any>(null);
  const { isAuthenticated } = useAuth();
  const [modalOpened, setModalOpened] = useState(false);
  const [expressInterestModalOpened, { open: openExpressInterestModal, close: closeExpressInterestModal }] =
    useDisclosure(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [acceptModalOpened, setAcceptModalOpened] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [removeModalOpened, { open: openRemoveModal, close: closeRemoveModal }] = useDisclosure(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [anonymizedPdfLoading, setAnonymizedPdfLoading] = useState(false);

  const {
    isLoggedIn,
    userDetails,
    shortlistedIds,
    addToShortlist,
    removeFromShortlist,
    hasCollaborationWith,
    addInterestedCompany,
    removeInterestedCompany,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  } = useUser();

  const refreshCompany = useCallback(async () => {
    // If companyData is provided, use it directly
    if (companyData) {
      setCompany(companyData);
      setLoading(false);
      return;
    }

    if (!id) {
      navigate("/");
      return;
    }

    setLoading(true);

    try {
      const response = await trpc.company.publicGetById.query({ companyId: id });
      setCompany(response);
    } catch (err) {
      console.error("Error fetching company:", err);
      // For public routes, don't call handleTrpcError to avoid login redirects
      if (pageID === "DISCOVER_PUBLIC") {
        navigate("/");
      } else {
        // For authenticated routes, use error handler
        handleTrpcError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [companyData, id, navigate, pageID]);

  // Check shortlist status from context
  useEffect(() => {
    if (disableShortlist) return;
    const companyIdToCheck = companyData?.id || id;
    if (companyIdToCheck) {
      setIsFavorited(shortlistedIds.has(companyIdToCheck));
    }
  }, [shortlistedIds, id, companyData, disableShortlist]);

  // Fetch collaboration status only if company ID exists in interestedCompanyIds
  useEffect(() => {
    if (!isAuthenticated || disableShortlist) return;
    const companyIdToCheck = companyData?.id || id;
    if (!companyIdToCheck) return;

    // Only fetch if we know there's a collaboration
    if (hasCollaborationWith(companyIdToCheck)) {
      trpc.collaboration.getByCompanyId
        .query({ targetCompanyId: companyIdToCheck })
        .then((collaboration) => {
          setCollaborationStatus(collaboration);
        })
        .catch((err) => {
          console.error("Error fetching collaboration:", err);
          setCollaborationStatus(null);
        });
    } else {
      setCollaborationStatus(null);
    }
  }, [isAuthenticated, id, companyData, hasCollaborationWith, disableShortlist]);

  const refetchCollaborationAfterPayment = useCallback(() => {
    const companyIdToCheck = companyData?.id || id;
    if (!companyIdToCheck || !isAuthenticated || disableShortlist) return;
    trpc.collaboration.getByCompanyId
      .query({ targetCompanyId: companyIdToCheck })
      .then((collaboration) => {
        setCollaborationStatus(collaboration);
      })
      .catch(() => { });
    refreshPendingInterestCount();
    refreshCollaborationSidebarCounts();
  }, [
    companyData?.id,
    id,
    isAuthenticated,
    disableShortlist,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  ]);

  const {
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    beginEngagementPayment,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
  } = useCollaborationEngagementPayment(refetchCollaborationAfterPayment);

  const bothSignedNda = collaborationStatus?.requesterSignedNDA && collaborationStatus?.receiverSignedNDA;

  const isPaymentPhase =
    Boolean(collaborationStatus?.paymentDeadline) &&
    (collaborationStatus?.status === CollaborationStatus.BOTH_ACCEPTED ||
      collaborationStatus?.status === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaborationStatus?.status === CollaborationStatus.RECEIVER_PAID_ONLY);

  const { displayMs, modalOpen, handleExtend, handleModalDismiss } = usePaymentWindowUiDemo(
    collaborationStatus?.id,
    collaborationStatus?.paymentDeadline,
    isPaymentPhase,
  );

  const userCompanyId = userDetails?.company?.id;
  const canIncomingReceiverDownloadAnonymizedPdf =
    Boolean(
      company &&
      isLoggedIn &&
      collaborationStatus?.status === CollaborationStatus.REQUESTED &&
      userCompanyId === collaborationStatus.receiverCompanyId &&
      company.id === collaborationStatus.requesterCompanyId
    );

  const handleDownloadAnonymizedProfilePdf = useCallback(async () => {
    if (!company) return;
    setAnonymizedPdfLoading(true);
    try {
      await downloadAnonymizedProfilePdf(company, collaborationStatus);
    } catch (err) {
      errorNotification({
        title: "Could not create PDF",
        message: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setAnonymizedPdfLoading(false);
    }
  }, [company, collaborationStatus]);

  // Initial fetch effect
  useEffect(() => {
    refreshCompany();
  }, [refreshCompany]);

  const handleBackClick = () => {
    const isDiscoverProfile = pageID === "DISCOVER_PUBLIC" || pageID === "DISCOVER_LOGGED_IN";
    if (isDiscoverProfile) {
      const fromState = (location.state as { discoverReturnTo?: string } | null)?.discoverReturnTo;
      if (fromState) {
        navigate(fromState);
        return;
      }

      // Fallback: if profile URL still carries filters, preserve them when going back.
      if (location.search) {
        navigate({ pathname: pageID === "DISCOVER_LOGGED_IN" ? "/my-discovery" : "/", search: location.search });
        return;
      }
    }

    if (backLink) {
      navigate(backLink);
    } else if (isLoggedIn) {
      navigate("/my-discovery");
    } else {
      navigate("/");
    }
  };


  const handleShortlistClick = async () => {
    if (!isAuthenticated) {
      setModalOpened(true);
      return;
    }

    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    if (!isFavorited) {
      // Add to shortlist
      try {
        await addToShortlist(companyIdToUse);
        successNotification({
          title: "Success",
          message: "Company added to shortlist",
        });
      } catch (err) {
        handleTrpcError(err);
      }
    } else {
      openRemoveModal();
    }
  };

  const handleRemoveConfirm = async () => {
    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    setRemoveLoading(true);
    try {
      await removeFromShortlist(companyIdToUse);
      successNotification({
        title: "Success",
        message: "Company removed from shortlist",
      });
      closeRemoveModal();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleSignUpClick = () => {
    navigate("/signup");
  };

  const handleBrowseCompaniesClick = () => {
    navigate("/");
  };

  const handleExpressInterestClick = () => {
    if (!isAuthenticated) {
      navigate("/signup");
      return;
    }
    openExpressInterestModal();
  };

  const handleConfirmExpressInterest = async (data: ExpressInterestFormData) => {
    const companyIdToUse = companyData?.id || id;
    if (!companyIdToUse) return;

    setRequestLoading(true);
    try {
      await trpc.collaboration.request.mutate({
        targetCompanyId: companyIdToUse,
        requesterRole: data.requesterRole,
        expectedPartnerRole: data.expectedPartnerRole,
        expressInterestTitle: data.expressInterestTitle ?? undefined,
        expressInterestSummary: data.expressInterestSummary ?? undefined,
      });
      refreshPendingInterestCount();
      successNotification({
        title: "Success",
        message: "Your interest has been expressed to the company",
      });
      closeExpressInterestModal();

      // Add to interested companies and fetch fresh collaboration
      addInterestedCompany(companyIdToUse);
      const collaboration = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyIdToUse });
      setCollaborationStatus(collaboration);
    } catch (error) {
      handleTrpcError(error);
      closeExpressInterestModal();
    } finally {
      setRequestLoading(false);
    }
  };

  const handleAcceptConfirm = async () => {
    if (!collaborationStatus?.id) return;

    setAcceptLoading(true);
    try {
      await trpc.collaboration.accept.mutate({ matchId: collaborationStatus.id });
      successNotification({
        title: "Success",
        message: "Collaboration request accepted!",
      });
      setAcceptModalOpened(false);

      const companyIdToUse = companyData?.id || id;
      if (companyIdToUse) {
        // Fetch fresh collaboration data
        const collaboration = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyIdToUse });
        setCollaborationStatus(collaboration);
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!collaborationStatus?.id) return;

    setDeclineLoading(true);
    try {
      await trpc.collaboration.reject.mutate({ matchId: collaborationStatus.id });
      successNotification({ title: "Success", message: "Collaboration request declined." });

      const companyIdToUse = companyData?.id || id;
      if (companyIdToUse) {
        // Remove from interested companies and clear collaboration status
        removeInterestedCompany(companyIdToUse);
        setCollaborationStatus(null);
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDeclineLoading(false);
    }
  };

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>Company not found</Text>
      </Container>
    );
  }

  const showProfileVerificationStepper =
    pageID === "MY_PROFILE_PREVIEW" &&
    ["DRAFT", "UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED", "ACTION_REQUIRED"].includes(company.status);

  return (
    <Box py="sm">
      {/* Mobile: Back Link */}
      {!hideBackButton && (
        <Box py="sm" hiddenFrom="sm">
          <Container size={containerSize}>
            <Button
              variant="filled"
              color="primary"
              leftSection={<IconArrowLeft size={16} />}
              size="sm"
              radius="xl"
              onClick={handleBackClick}
            >
              {backText || "Back to Discovery"}
            </Button>
          </Container>
        </Box>
      )}

      {/* Alert Banner */}
      {!hideInfoBanner && !isLoggedIn && (
        <Box bg={STATUS_COLORS.WARNING.bg} w="100%">
          <Container size={containerSize}>
            <InfoBanner
              icon={IconLock}
              iconSize={18}
              iconColor={STATUS_COLORS.WARNING.icon}
              title="Sign up to unlock full company details and express interest"
              subtitle="View contact information, download documents, and connect with this company"
              bgColor="transparent"
              titleColor={STATUS_COLORS.WARNING.text}
              subtitleColor={STATUS_COLORS.WARNING.text}
              borderColor="transparent"
            />
          </Container>
        </Box>
      )}

      {/* Desktop: Header Navigation */}
      {!hideBackButton && (
        <Box py="sm" visibleFrom="sm">
          <Container size={containerSize}>
            <Flex justify="space-between" align="center">
              <Button
                variant="filled"
                color="primary"
                leftSection={<IconArrowLeft size={16} />}
                size="sm"
                radius="xl"
                onClick={handleBackClick}
              >
                {backText || "Back to Discovery"}
              </Button>
            </Flex>
          </Container>
        </Box>
      )}

      {/* Company Header */}
      <Container size={containerSize}>
        <CompanyHeaderCard
          company={company}
          headquartersFlagEmoji={headquartersFlagEmoji}
          showProfileVerificationStepper={showProfileVerificationStepper}
          hideInterestCTA={hideInterestCTA}
          disableShortlist={disableShortlist}
          isFavorited={isFavorited}
          collaborationStatus={collaborationStatus}
          userCompanyId={userDetails?.company?.id}
          canIncomingReceiverDownloadAnonymizedPdf={canIncomingReceiverDownloadAnonymizedPdf}
          anonymizedPdfLoading={anonymizedPdfLoading}
          declineLoading={declineLoading}
          onExpressInterestClick={handleExpressInterestClick}
          onShortlistClick={handleShortlistClick}
          onAcceptRequestClick={() => setAcceptModalOpened(true)}
          onDeclineClick={handleDecline}
          onDownloadAnonymizedProfilePdf={handleDownloadAnonymizedProfilePdf}
          showDownloadProfilePdf={isLoggedIn && !collaborationStatus && !hideInterestCTA}
          bookmarkButtonStyles={BOOKMARK_HEADER_BTN_STYLES}
        />

        <WhatWeOfferCard value={company.customerOfferHighlight} />

        {/* Payment deadline warning — last 1 minute of the 3-minute window (demo timings) */}
        {!hideInfoBanner &&
          collaborationStatus?.paymentDeadline &&
          displayMs != null &&
          displayMs > 0 &&
          isPaymentUiWarningUrgent(displayMs) && (
            <Box mb="md">
              <InfoBanner
                icon={IconExclamationCircle}
                iconColor={STATUS_COLORS.WARNING.text}
                title={`Payment window expires in ${formatPaymentHoursLabel(displayMs)}`}
                subtitle="Both parties must complete payment within 96 hours of the first payment."
                bgColor={STATUS_COLORS.WARNING.bg}
                borderColor={STATUS_COLORS.WARNING.border}
                titleColor={STATUS_COLORS.WARNING.text}
                subtitleColor={STATUS_COLORS.WARNING.text}
              />
            </Box>
          )}

        {/* Payment Done InfoBanner - Shows when BOTH_PAID */}
        {!hideInfoBanner && collaborationStatus?.status === "BOTH_PAID" && (
          <Box mb="md">
            <Paper
              p="md"
              radius="lg"
              bg={STATUS_COLORS.SUCCESS.bg}
              style={{ border: `1px solid ${STATUS_COLORS.SUCCESS.border}` }}
            >
              <Flex justify="space-between" align="center" gap="md" wrap="wrap">
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={600} c={STATUS_COLORS.SUCCESS.text} mb={4}>
                    Payment Done
                  </Text>
                  <Text size="xs">
                    {bothSignedNda
                      ? "Both parties have completed payment and accepted the NDA."
                      : "Both parties have completed payment. NDA acceptance is recorded from payment."}
                  </Text>
                </Box>
                <Button
                  size="sm"
                  color={STATUS_COLORS.SUCCESS.text}
                  onClick={() => navigate(`/collaborations/${collaborationStatus.id}`)}
                >
                  Open Workspace
                </Button>
              </Flex>
            </Paper>
          </Box>
        )}

        {/* Payment Done InfoBanner - Shows when REQUESTER_PAID_ONLY and user is requester */}
        {!hideInfoBanner &&
          collaborationStatus?.status === "REQUESTER_PAID_ONLY" &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            const isRequester = collaborationStatus.requesterCompanyId === userCompanyId;

            // Only show this banner if the viewer is the requester who paid
            if (isRequester) {
              return (
                <Box mb="md">
                  <InfoBanner
                    title="Payment Done"
                    subtitle="You've completed payment. Waiting for the collaborator to finish theirs."
                    bgColor={STATUS_COLORS.SUCCESS.bg}
                    borderColor={STATUS_COLORS.SUCCESS.border}
                    titleColor={STATUS_COLORS.SUCCESS.text}
                    subtitleColor={STATUS_COLORS.SUCCESS.text}
                  />
                </Box>
              );
            }
            return null;
          })()}

        {/* Payment Done InfoBanner - Shows when RECEIVER_PAID_ONLY and user is receiver */}
        {!hideInfoBanner &&
          collaborationStatus?.status === "RECEIVER_PAID_ONLY" &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            const isReceiver = collaborationStatus.receiverCompanyId === userCompanyId;

            // Only show this banner if the viewer is the receiver who paid
            if (isReceiver) {
              return (
                <Box mb="md">
                  <InfoBanner
                    title="Payment Done"
                    subtitle="You've completed payment. Waiting for the collaborator to finish theirs."
                    bgColor={STATUS_COLORS.SUCCESS.bg}
                    borderColor={STATUS_COLORS.SUCCESS.border}
                    titleColor={STATUS_COLORS.SUCCESS.text}
                    subtitleColor={STATUS_COLORS.SUCCESS.text}
                  />
                </Box>
              );
            }
            return null;
          })()}

        {/* Ready to Collaborate Banner - Shows when BOTH_ACCEPTED, or when user hasn't paid yet */}
        {!hideInterestCTA &&
          isLoggedIn &&
          collaborationStatus &&
          (() => {
            const userCompanyId = userDetails?.company?.id;
            if (!userCompanyId) return false;

            const isRequester = collaborationStatus.requesterCompanyId === userCompanyId;
            const userPaid = isRequester ? collaborationStatus.requesterPaid : collaborationStatus.receiverPaid;

            // Show banner if:
            // 1. Status is BOTH_ACCEPTED and user hasn't paid
            // 2. Status is REQUESTER_PAID_ONLY and user is receiver (hasn't paid)
            // 3. Status is RECEIVER_PAID_ONLY and user is requester (hasn't paid)
            return (
              (collaborationStatus.status === "BOTH_ACCEPTED" && !userPaid) ||
              (collaborationStatus.status === "REQUESTER_PAID_ONLY" && !isRequester) ||
              (collaborationStatus.status === "RECEIVER_PAID_ONLY" && isRequester)
            );
          })() && (
            <Paper p="xl" radius="lg" mb="lg" bg="white" style={{ border: "1px solid #E5E7EB" }}>
              <Flex justify="space-between" align="center" gap="xl" wrap="wrap">
                <Box style={{ flex: 1 }}>
                  {collaborationStatus?.status === "RECEIVER_PAID_ONLY" ||
                    collaborationStatus?.status === "REQUESTER_PAID_ONLY" ? (
                    <Box
                      px={16}
                      py={7}
                      mb={4}
                      style={{
                        borderRadius: "var(--mantine-radius-md)",
                        backgroundColor: "var(--mantine-color-red-0)",
                        color: "var(--mantine-color-red-7)",
                        fontSize: "13px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        width: "fit-content",
                      }}
                    >
                      Payment Pending
                    </Box>
                  ) : (
                    <Text size="lg" fw={600} mb={4} c="gray.9">
                      Ready to Collaborate?
                    </Text>
                  )}
                  <Text size="sm" c="gray.6">
                    {collaborationStatus?.status === "RECEIVER_PAID_ONLY" ||
                      collaborationStatus?.status === "REQUESTER_PAID_ONLY"
                      ? "The collaborator has completed payment. Complete yours to proceed."
                      : "To collaborate and to unlock full contact details of this company, make payment."}
                  </Text>
                </Box>
                <Button
                  size="md"
                  color="primary"
                  onClick={() => beginEngagementPayment(collaborationStatus.id)}
                >
                  Make Payment
                </Button>
              </Flex>
            </Paper>
          )}

        {/* Protected Info Alert */}
        {!hideInfoBanner && !isLoggedIn && (
          <Box mb="md">
            <InfoBanner
              icon={IconLock}
              iconSize={24}
              title="Contact Information Protected"
              subtitle="Direct contact details (email, phone) are available after mutual interest is confirmed and payment is processed."
              rightSection={
                <Flex
                  direction={{ base: "column", sm: "row" }}
                  gap="sm"
                  justify="flex-end"
                  align={{ base: "stretch", sm: "center" }}
                  w={{ base: "100%", sm: "auto" }}
                >
                  <Box w={{ base: "100%", sm: "auto" }}>
                    <Button fullWidth size="sm" onClick={handleSignUpClick} color="primary" variant="filled">
                      Sign Up Free
                    </Button>
                  </Box>
                  <Box w={{ base: "100%", sm: "auto" }}>
                    <Button
                      fullWidth
                      variant="outline"
                      size="sm"
                      onClick={handleBrowseCompaniesClick}
                      bg="white"
                    >
                      Browse More Companies
                    </Button>
                  </Box>
                </Flex>
              }
            />
          </Box>
        )}

        <Box pt="xl">
          <ProfileTab
            company={company}
            collaboration={collaborationStatus}
            isRequester={
              Boolean(collaborationStatus) &&
              collaborationStatus.requesterCompanyId === userDetails?.company?.id
            }
            viewerCollaborationIntent={userDetails?.company?.collaborationIntent as string[] | undefined}
            onDownloadVerificationPdf={onDownloadVerificationPdf}
            verificationPdfDownloading={verificationPdfDownloading}
          />
        </Box>
      </Container>

      <ShortlistModal opened={modalOpened} onClose={() => setModalOpened(false)} companyName="Anonymous Company" />

      <ExpressInterestModal
        opened={expressInterestModalOpened}
        onClose={closeExpressInterestModal}
        onConfirm={handleConfirmExpressInterest}
        loading={requestLoading}
        targetCompanyId={company?.id ?? companyData?.id ?? id ?? ""}
        userWhoAmI={(userDetails?.company?.whoAmI as string[]) || []}
        targetCollaborationIntent={(company?.collaborationIntent as string[]) || []}
      />

      <GenericModal
        disclosure={[removeModalOpened, { open: openRemoveModal, close: closeRemoveModal, toggle: () => { } }]}
        title="Remove Shortlist"
        subtitle="Are you sure you want to remove this company from your shortlist?"
        buttons={[
          {
            label: "Remove",
            onClick: handleRemoveConfirm,
            variant: "filled",
            color: "primary",
            loading: removeLoading,
          },
        ]}
      />

      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={() => setAcceptModalOpened(false)}
        onConfirm={handleAcceptConfirm}
        isLoading={acceptLoading}
      />

      <PaymentWindowExtendModal
        opened={modalOpen}
        onExtend={handleExtend}
        onDismiss={handleModalDismiss}
      />

      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={handlePaymentModalClose}
        onConfirm={handlePaymentConfirm}
        loading={paymentLoading}
        creditsRequired={engagementFeeCredits}
      />
      <CollaborationPaymentSuccessModal
        opened={!!successData}
        onClose={handleSuccessModalClose}
        paymentData={successData}
      />
    </Box>
  );
}
